import { Dispatch, SetStateAction } from 'react';

import { AlertIcon, Label, Tooltip, TTranslator } from '@smartshell/ui-kit';
import type { TLabelColor } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { SessionRestTimer } from 'components';
import { Host } from 'hooks';
import { TLocalesKeys } from 'localization';
import { checkShellVersion, getActiveSeance, getHostStatus } from 'utils';

import { BookingsTooltip } from './BookingsTooltip';
import s from './ComputersTable.module.scss';
import { LockTooltip } from './LockTooltip';
import { OnlineBookingNotEnableTooltip } from './OnlineBookingNotEnableTooltip';

const shortenValue = (string: string, size: number): string =>
  string.length < size ? string : `${string.substring(0, size - 3)}...`;

const getLabelColor = (host: Host): TLabelColor => {
  const status = getHostStatus(host);

  switch (status) {
    case 'FREE':
      return host.online ? 'green' : 'gray-primary';
    case 'BUSY':
      return host.online ? 'blue' : 'red';
    case 'IN_SERVICE':
      return 'orange';
    default:
      return 'gray-primary';
  }
};
const compareVersions = (v1: string, v2: string): number => {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);

  const maxLength = Math.max(parts1.length, parts2.length);

  for (let i = 0; i < maxLength; i++) {
    const num1 = parts1[i] || 0;
    const num2 = parts2[i] || 0;

    if (num1 > num2) return 1;
    if (num1 < num2) return -1;
  }

  return 0;
};
export const formatHostTime = (time: string): string =>
  dayjs(time).format('HH:mm:ss');

export const getColumns: (
  setSelectedClientId: Dispatch<SetStateAction<string | null>>,
  t: TTranslator<TLocalesKeys>,
  shellVersion: string | null
) => Array<Column<Host>> = (setSelectedClientId, t, shellVersion) => [
  {
    Header: '№',
    accessor: 'position',
    disableResizing: true,
    width: 56,
  },
  {
    id: 'locked',
    accessor: 'locked',
    disableResizing: true,
    disableSortBy: true,
    width: 56,
    Cell: ({ value }) => value && <LockTooltip />,
  },
  {
    id: 'online_booking_enabled',
    accessor: 'online_booking_enabled',
    disableResizing: true,
    disableSortBy: true,
    width: 56,
    Cell: ({ value }) => !value && <OnlineBookingNotEnableTooltip />,
  },
  {
    Header: t('title'),
    accessor: 'alias',
    Cell: ({ value: alias, row }: { value: string; row: Row<Host> }) => (
      <span
        data-onboarding-host-id={row.original.id}
        data-onboarding={
          getHostStatus(row.original) === 'FREE' && row.original.online
            ? 'free-host'
            : undefined
        }
        style={{ pointerEvents: 'none' }}
        title={alias}
      >
        {alias}
      </span>
    ),
    width: 170,
  },
  {
    id: 'status',
    Header: t('status'),
    accessor: (host) => {
      const status = getHostStatus(host);

      switch (status) {
        case 'FREE':
          return t(
            host.online ? 'host.statuses.on' : 'host.statuses.noConnection'
          );
        case 'BUSY':
          return t(
            host.online ? 'host.statuses.busy' : 'host.statuses.noConnection'
          );
        case 'IN_SERVICE':
          return t('host.statuses.service');
        default:
          return t('host.statuses.noConnection');
      }
    },
    Cell: ({ value: status, row }: { value: string; row: Row<Host> }) => (
      <Label
        text={status}
        color={getLabelColor(row.original)}
        className={s.label}
      />
    ),
    width: 152,
  },
  {
    id: 'booking',
    Header: t('booking.contracted'),
    accessor: (host) => {
      if (host.bookings.length) {
        return t('host.statuses.booked');
      }

      return null;
    },
    Cell: ({ row }: { row: Row<Host> }) => (
      <BookingsTooltip bookings={row.original.bookings} />
    ),
    width: 120,
  },
  {
    id: 'client',
    Header: t('client'),
    accessor: (host) => {
      const hasSession = !!host.client_sessions.length;

      if (hasSession) {
        return host.user?.nickname ?? t('client.guest');
      }

      return host.user?.nickname ?? null;
    },
    Cell: ({ value, row }: { value: string; row: Row<Host> }) => {
      const uuid = row.original.user?.uuid;
      if (!uuid) return t('client.guest');

      return (
        <span
          title={value}
          role="button"
          tabIndex={0}
          className={s.user}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedClientId(uuid);
          }}
        >
          {shortenValue(value, 16)}
        </span>
      );
    },
    width: 212,
  },
  {
    id: 'seance',
    Header: t('seance'),
    accessor: ({ client_sessions: [activeSession] }) => {
      const activeSeance = getActiveSeance(activeSession?.seances || []);

      return activeSeance?.tariff.title;
    },
    width: 170,
  },
  {
    id: 'start',
    Header: t('host.seance.start'),
    accessor: ({ client_sessions: [activeSession] }) =>
      activeSession?.started_at,
    Cell: ({ value }: { value: string }) => formatHostTime(value),
    width: 120,
  },
  {
    id: 'finish',
    Header: t('host.seance.finish'),
    accessor: ({ client_sessions: [activeSession] }) =>
      activeSession?.finished_at
        ? dayjs(activeSession.finished_at).toISOString()
        : null,
    Cell: ({ value }: { value: string }) => formatHostTime(value),
    width: 125,
  },
  {
    id: 'rest',
    Header: t('host.seance.rest'),
    accessor: ({ client_sessions: [activeSession] }) => {
      if (!activeSession) {
        return null;
      }

      return activeSession.time_left;
    },
    Cell: ({ value, row }: { value: number; row: Row<Host> }) => {
      const activeSession = row.original.client_sessions[0];

      if (!activeSession) return null;

      return activeSession.finished_at ? (
        <SessionRestTimer
          rest={value}
          sessionId={activeSession.id}
          postpaid={activeSession.postpaid}
          total_cost={activeSession.total_cost}
          showMinimal={false}
        />
      ) : (
        '∞'
      );
    },
    width: 120,
  },
  {
    id: 'application',
    Header: t('host.application'),
    accessor: ({ counters }) => counters?.active_window,
    Cell: ({ value: application }: { value: string }) => (
      <span title={application}>{application}</span>
    ),
    width: 220,
  },
  {
    id: 'version',
    Header: t('host.version'),
    accessor: ({ info }) => info?.shell_version,
    Cell: ({ value: version }: { value: string }) => {
      const isOutdated =
        shellVersion &&
        version &&
        checkShellVersion(version) &&
        compareVersions(version, shellVersion) < 0;

      return (
        <div className={s.version_block}>
          <span title={version}>{version}</span>
          {isOutdated && (
            <Tooltip
              position={'bottom-right'}
              content={t('shell.version.tooltip', shellVersion)}
              contentClassName={s.tooltip}
            >
              <AlertIcon color="warning" size="s" />
            </Tooltip>
          )}
        </div>
      );
    },
    width: 126,
  },
];
